var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '../icon/Icon.js';
/**
 * Floating popover used to change a status inline (e.g. clicking the
 * status pill on a scenario card). Positioned at viewport coordinates so
 * it escapes scroll/overflow containers.
 *
 * Imperative usage — instantiate, configure, then `openAt(anchorElement)`.
 * The popover appends itself to `<body>` and auto-removes on close.
 *
 * Events:
 *  - `status-change` { value: string } — emitted when the user picks an
 *    option. The popover then closes.
 *  - `cancel` — emitted when the user dismisses without selecting (click
 *    outside, Escape).
 */
let StatusPickerPopover = class StatusPickerPopover extends LitElement {
    constructor() {
        super(...arguments);
        this.options = [];
        this._top = 0;
        this._left = 0;
        this._open = false;
        this._onDocClick = (e) => {
            if (!this._open)
                return;
            const path = e.composedPath();
            if (!path.includes(this)) {
                this._cancel();
            }
        };
        this._onKey = (e) => {
            if (e.key === 'Escape' && this._open) {
                e.stopPropagation();
                this._cancel();
            }
        };
    }
    /** Anchor the popover next to `anchor`, append to body, and open. */
    openAt(anchor) {
        if (!this.isConnected)
            document.body.appendChild(this);
        this._reposition(anchor);
        this._open = true;
        this.setAttribute('open', '');
        // Defer listeners by a tick so the click that opened us doesn't
        // immediately close us via the outside-click handler.
        setTimeout(() => {
            document.addEventListener('mousedown', this._onDocClick, true);
            document.addEventListener('keydown', this._onKey, true);
        }, 0);
    }
    /** Close + remove from DOM. */
    close() {
        if (!this._open)
            return;
        this._open = false;
        this.removeAttribute('open');
        document.removeEventListener('mousedown', this._onDocClick, true);
        document.removeEventListener('keydown', this._onKey, true);
        this.remove();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('mousedown', this._onDocClick, true);
        document.removeEventListener('keydown', this._onKey, true);
    }
    /** Compute and store viewport-anchored coordinates so the popover sits
     *  below `anchor`, right-aligned to it, and flips above if it would
     *  overflow the viewport. */
    _reposition(anchor) {
        const rect = anchor.getBoundingClientRect();
        // Estimate our own size (we may not be measured yet — fall back to
        // sensible defaults that match the CSS).
        const estW = Math.max(this.offsetWidth, 160);
        const estH = Math.max(this.offsetHeight, 160);
        const margin = 6;
        let top = rect.bottom + margin;
        let left = rect.right - estW;
        if (top + estH > window.innerHeight - margin) {
            // Not enough room below — flip above the anchor.
            top = rect.top - margin - estH;
        }
        if (left < margin)
            left = margin;
        if (left + estW > window.innerWidth - margin) {
            left = window.innerWidth - estW - margin;
        }
        this._top = Math.max(margin, top);
        this._left = left;
        this.style.top = `${this._top}px`;
        this.style.left = `${this._left}px`;
    }
    _cancel() {
        this.dispatchEvent(new CustomEvent('cancel', { bubbles: true, composed: true }));
        this.close();
    }
    _onSelect(value, e) {
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent('status-change', {
            detail: { value },
            bubbles: true,
            composed: true
        }));
        this.close();
    }
    _renderOption(opt) {
        const active = opt.value === this.value;
        return html `
      <button
        type="button"
        class="option ${active ? 'is-active' : ''}"
        @click=${(e) => this._onSelect(opt.value, e)}
      >
        ${opt.dotColor
            ? html `<span class="dot" style="background:${opt.dotColor};"></span>`
            : ''}
        <span class="label">
          ${opt.label}
          ${opt.locked
            ? html `<span class="lock"><basic-icon name="lock" size="9"></basic-icon></span>`
            : ''}
        </span>
        <span class="check">
          <basic-icon name="check" size="12"></basic-icon>
        </span>
      </button>
    `;
    }
    render() {
        return html `${this.options.map((o) => this._renderOption(o))}`;
    }
};
StatusPickerPopover.styles = css `
    :host {
      position: fixed;
      display: none;
      flex-direction: column;
      min-width: 160px;
      padding: 4px;
      background: #fff;
      border: 1px solid #d0d7de;
      border-radius: 8px;
      box-shadow:
        0 6px 20px rgba(0, 0, 0, 0.12),
        0 2px 4px rgba(0, 0, 0, 0.05);
      z-index: 1000;
      font-family: inherit;
      font-size: 12.5px;
    }
    :host([open]) {
      display: flex;
    }

    .option {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 7px 10px;
      background: none;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      font-family: inherit;
      font-size: 12.5px;
      color: #333;
      text-align: left;
    }
    .option:hover {
      background: #f4f7fa;
    }
    .option.is-active {
      background: #f2f6fa;
    }
    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
    }
    .label {
      flex: 1;
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }
    .lock {
      flex-shrink: 0;
      color: currentColor;
      opacity: 0.85;
      display: inline-flex;
    }
    .check {
      color: #0078d4;
      flex-shrink: 0;
      visibility: hidden;
      display: inline-flex;
    }
    .option.is-active .check {
      visibility: visible;
    }
  `;
__decorate([
    property({ type: Array })
], StatusPickerPopover.prototype, "options", void 0);
__decorate([
    property({ type: String })
], StatusPickerPopover.prototype, "value", void 0);
__decorate([
    state()
], StatusPickerPopover.prototype, "_top", void 0);
__decorate([
    state()
], StatusPickerPopover.prototype, "_left", void 0);
__decorate([
    state()
], StatusPickerPopover.prototype, "_open", void 0);
StatusPickerPopover = __decorate([
    customElement('status-picker-popover')
], StatusPickerPopover);
export { StatusPickerPopover };
//# sourceMappingURL=StatusPickerPopover.js.map